import type { FieldInputProps } from "formik";
import { isNil, range } from "lodash";
import type { TriggerSummaryAverage } from "src/api/marketing-actions";
import type { IdName } from "src/types/IdName";
import { TriggerRefValueTypeEnum } from "src/types/action/MarketingActionSlot";
import { TriggerMetricEnum } from "src/types/trigger";
import {
    METRIC_FACTOR,
    PERCENT_FACTOR,
    formatTriggerPercent,
    formatTriggerThreshold,
} from "src/utils/formatters";
import type { OptionType } from "src/views/marketing-actions/components/form/ConditionCard/ConditionCard";
import { MAX_EDGE_VALUE } from "src/views/marketing-actions/components/form/ScenarioCard/AddScenarioDialog";
import { formatPercentValueForScenarios } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioUtils";

export const INFINITE_TAG = "∞";

export const buildTriggerPercentOptions = (): IdName[] => {
    const percentOptions: IdName[] = [];
    range(-25, 30, 5).forEach((percent) => {
        percentOptions.push({ id: percent, name: formatTriggerPercent(percent) });
    });
    return percentOptions;
};

export const buildTriggerPercentScenarioOptions = (): IdName[] => {
    return [
        { id: 0, name: formatPercentValueForScenarios(0) },
        { id: 25, name: formatPercentValueForScenarios(25) },
        { id: 50, name: formatPercentValueForScenarios(50) },
        { id: 75, name: formatPercentValueForScenarios(75) },
        { id: 100, name: formatPercentValueForScenarios(100) },
        { id: 120, name: formatPercentValueForScenarios(120) },
        { id: MAX_EDGE_VALUE, name: `+${INFINITE_TAG}%` },
    ];
};

export function addHundredDelta(percent: number): number {
    if (isNil(percent)) {
        return percent;
    }
    return percent + 100;
}

export function subtractHundredDelta(percent: number): number {
    if (isNil(percent)) {
        return percent;
    }
    return percent - 100;
}

export const optionBuilderForTriggerScenarioValues = (
    field: FieldInputProps<any>,
    option: OptionType[]
): OptionType[] => {
    const array = [
        ...(field.value && option.findIndex((po) => po.id === field.value) === -1
            ? [
                  {
                      id: field.value,
                      name: formatTriggerThreshold(field.value, null),
                  },
              ]
            : []),
        ...option,
    ].sort((a, b) => {
        return a.id - b.id;
    });
    return array;
};

interface ComputeSummaryAverageProps {
    values: any;
    triggerSummaryAverage: TriggerSummaryAverage;
}

export function computeTriggerSummaryAverage({
    values,
    triggerSummaryAverage,
}: ComputeSummaryAverageProps): number {
    const summaryAverage = Math.round(
        (values.metric === TriggerMetricEnum.DailyOrders
            ? triggerSummaryAverage?.summaryTotalOrders
            : triggerSummaryAverage?.summaryTotalSales) / METRIC_FACTOR[values.metric]
    );
    const percent = values?.metaData?.thresholdFactor / PERCENT_FACTOR;
    const value = Math.round(summaryAverage * (1 + percent));
    return value;
}

export function addThresholdToTriggerDisplayValue({
    type,
    threshold,
    value,
}: {
    type: TriggerRefValueTypeEnum;
    threshold: number;
    value: number;
}): number {
    let percentage = null;
    if (type === TriggerRefValueTypeEnum.ReferenceValue || isNil(type)) {
        percentage = threshold;
    }
    if (percentage === null) {
        return value;
    }
    const compute = value * (1 + percentage / 100);
    return compute;
}
